
import Vue from "vue";
import SliderInput from "@/components/atoms/SliderInput.vue";
import { useEditorStyleSettings } from "@/composables/useEditorStyleSettings";
import type { EditorStyleSettings } from "@/composables/useEditorStyleSettings";

const SAMPLE_TEXT =
  "吾輩は猫である。名前はまだ無い。どこで生れたかとんと見当がつかぬ。何でも薄暗いじめじめした所でニャーニャー泣いていた事だけは記憶している。";

export default Vue.extend({
  name: "EditorStyleSettingsModal",
  components: { SliderInput },
  props: {
    /** モーダルを開いた画面（analytics用）: 'writing' | 'multi_writing' */
    source: {
      type: String as () => "writing" | "multi_writing",
      default: "writing",
    },
  },
  setup() {
    const { settings, loadSettings, saveSettings, defaults, limits } = useEditorStyleSettings();
    return {
      settings,
      loadSettings,
      saveSettings,
      defaults,
      limits,
    };
  },
  data() {
    return {
      sampleText: SAMPLE_TEXT,
      /** モーダル内の下書き（プレビュー・スライダーはこれに紐づけ Vue 2 の reactivity で確実に更新する） */
      draft: {
        fontSize: 12,
        lineHeight: 1.8,
        letterSpacing: 0,
      },
    };
  },
  computed: {
    formFontSize(): number {
      return this.draft.fontSize;
    },
    formLineHeight(): number {
      return this.draft.lineHeight;
    },
    formLetterSpacing(): number {
      return this.draft.letterSpacing;
    },
    previewStyle(): Record<string, string | number> {
      return {
        fontSize: `${this.draft.fontSize}pt`,
        lineHeight: this.draft.lineHeight,
        letterSpacing: `${this.draft.letterSpacing}pt`,
      };
    },
  },
  mounted() {
    this.loadSettings();
    this.syncDraftFromSettings();
    this.trackModalOpen();
  },
  methods: {
    syncDraftFromSettings() {
      const s = (this.settings as { value: EditorStyleSettings }).value;
      this.draft.fontSize = s.fontSize;
      this.draft.lineHeight = s.lineHeight;
      this.draft.letterSpacing = s.letterSpacing;
    },
    updateFontSize(v: number) {
      this.draft.fontSize = v;
    },
    updateLineHeight(v: number) {
      this.draft.lineHeight = v;
    },
    updateLetterSpacing(v: number) {
      this.draft.letterSpacing = v;
    },
    trackModalOpen() {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          event: "editor_style_modal_open",
          source: this.source,
        });
      }
    },
    trackApply() {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          event: "editor_style_changed",
          font_size: this.draft.fontSize,
          line_height: this.draft.lineHeight,
          letter_spacing: this.draft.letterSpacing,
          source: this.source,
        });
      }
    },
    trackReset() {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          event: "editor_style_reset",
          source: this.source,
        });
      }
    },
    trackCancel() {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          event: "editor_style_cancel",
          source: this.source,
        });
      }
    },
    handleApply() {
      this.saveSettings(this.draft);
      this.trackApply();
      this.$close(true);
    },
    /** モーダル内の設定値のみデフォルトに戻す。保存は適用ボタンで行う。 */
    handleReset() {
      this.draft.fontSize = this.defaults.fontSize;
      this.draft.lineHeight = this.defaults.lineHeight;
      this.draft.letterSpacing = this.defaults.letterSpacing;
      this.trackReset();
    },
    handleCancel() {
      this.trackCancel();
      this.$close(false);
    },
  },
});
